import { memo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import deepEqual from 'fast-deep-equal/es6/react';

import { Menu, MenuButton, MenuList, Button, MenuOptionGroup, MenuItemOption } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';

import { ApplicationState } from '@/store';
import { getAssignStagesDropdownOptions } from 'shared-components';
import { bulkAction } from '@/store/dashboard/actions';
import { BulkActionTypeEnum } from '@/store/studentUsers/types';
import { CoreStatusExtended, IOption } from 'api-paths-cas';
import { useSearchParams } from 'react-router-dom';

export const MoveTo = () => {
  const dispatch = useDispatch();
  const defaultOptionRef = useRef();
  const [search = '?stage=Post-Deposit'] = useSearchParams();

  const getIsMoveToDropdownDisabled = (state: ApplicationState) => {
    const { isDropdownDisabled = true } = state.dashboard;
    const params = new URLSearchParams(search);
    return isDropdownDisabled || params.get('stage') === CoreStatusExtended.WAITING_FOR_INTAKE;
  };

  const { moveToStages, isPathwayProvider, isDropdownDisabled, selectedStatus, isAllPathwayStudents, processedByUni } =
    useSelector((state: ApplicationState) => {
      const { config, dashboard } = state;
      const { students = [], selectedRows, allSelected } = dashboard;
      const selectedStudents = students.filter(value => selectedRows.indexOf(value.p_key) > -1);
      return {
        moveToStages: config.moveToStages,
        isPathwayProvider: (config?.pathwayIds?.length ?? 0) > 0,
        isDropdownDisabled: getIsMoveToDropdownDisabled(state),
        selectedStudents,
        selectedStatus: {
          label: selectedStudents[0]?.status,
          value: selectedStudents[0]?.status,
        },
        selectedStudentsIds: selectedStudents.map(student => student.p_key),
        isAllPathwayStudents: selectedStudents.every(student => student.pathway),
        processedByUni: config.processedByUni ?? false,
        allSelected,
      };
    }, deepEqual);

  const StatusDropdownOptions = useCallback(
    //@ts-ignore
    () =>
      getAssignStagesDropdownOptions(
        moveToStages?.map(config => config.label ?? []),
        isAllPathwayStudents,
        isPathwayProvider,
        processedByUni,
      ),
    [moveToStages, isAllPathwayStudents, isPathwayProvider, processedByUni],
  );
  const onChangeMoveTo = useCallback(
    option => {
      const moveToOption = moveToStages?.find(stage => stage.label === option.value);
      if (moveToOption?.key) {
        dispatch(bulkAction(BulkActionTypeEnum.ASSIGN_STAGE, moveToOption.key));
      } else {
        console.error(`No key found for stage: ${option.value}`);
      }
    },
    [dispatch],
  );
  return (
    <>
      <Menu initialFocusRef={defaultOptionRef}>
        <MenuButton
          as={Button}
          size="sm"
          variant="solid"
          disabled={isDropdownDisabled}
          rightIcon={<FontAwesomeIcon className="utility-icon" icon={regular('angle-down')} />}
          leftIcon={<FontAwesomeIcon className="utility-icon" icon={regular('circle-arrow-right')} />}
        >
          Move to
        </MenuButton>
        <MenuList fontSize="sm" data-selector="move-to-dropdown">
          <MenuOptionGroup defaultValue={selectedStatus.value} type="radio">
            {StatusDropdownOptions().map((option: IOption, index: number) => (
              <MenuItemOption
                key={`move-to-${index}`}
                ref={option.value === selectedStatus.value ? defaultOptionRef : null}
                value={option.value}
                onClick={() => onChangeMoveTo(option)}
              >
                {option.label}
              </MenuItemOption>
            ))}
          </MenuOptionGroup>
        </MenuList>
      </Menu>
    </>
  );
};

export const MoveToComp = memo(MoveTo, deepEqual);
